import { isProd } from "@/lib/utils";
import Head from "next/head";

export const getSeo = (path: string, title?: string, description?: string, image?: string) => {
  if (path.startsWith("/")) {
    path = path.slice(1);
  }

  if (path.includes("/")) {
    path = path.split("/")[0];
  }

  const content = {
    name: "DollarDeploy" + (isProd() ? "" : " (dev)"),
    title: "Self-host in One Click",
    motto: "Your App. Your Server. Zero DevOps.",
    description:
      "Stop paying Vercel 20+/mo per seat. Deploy your Next.js, Go, or Python app to your own server with automatic HTTPS and builds on every GitHub push. No Docker, no cold starts, no lock‑in. Start free.",
    image: "/cover.jpg"
  };

  if (path === "dashboard") {
    Object.assign(content, {
      title: "Dashboard",
      description: "Quick overview of your apps and hosts, and latest deployments.",
      image: "/api/og/dashboard"
    });
  }

  if (path === "settings") {
    Object.assign(content, {
      title: "Settings",
      description: "Settings for DollarDeploy",
      image: "/api/og/settings"
    });
  }

  if (path === "backup") {
    Object.assign(content, {
      title: "Backup",
      description: "Backups of your apps and hosts.",
      image: "/api/og/backup"
    });
  }

  if (path === "journal") {
    Object.assign(content, {
      title: "History",
      description: "Journal of activity and logs of your apps and hosts.",
      image: "/api/og/journal"
    });
  }

  if (path === "task") {
    Object.assign(content, {
      title: "Tasks",
      description: "Tasks for your apps and hosts.",
      image: "/api/og/task"
    });
  }

  if (path === "host") {
    Object.assign(content, {
      title: "Servers",
      description: "Servers you manage and deploy your apps to.",
      image: "/api/og/host"
    });
  }

  if (path === "app") {
    Object.assign(content, {
      title: "Application",
      description: "App details and management, backup, build and deployment.",
      image: "/api/og/app"
    });
  }

  if (path === "r") {
    Object.assign(content, {
      title: "Templates",
      description: "Deploy from templates in one click to your server.",
      image: "/api/og/r"
    });
  }

  if (path === "pricing") {
    Object.assign(content, {
      title: "Pricing",
      description:
        "Simple pricing from $5/mo. No per-request fees, no surprise bills. Deploy unlimited apps to your own VPS.",
      image: "/api/og/pricing"
    });
  }

  if (path === "faq") {
    Object.assign(content, {
      title: "FAQ",
      description: "Frequently asked questions about DollarDeploy",
      image: "/api/og/faq"
    });
  }

  if (path === "privacy") {
    Object.assign(content, {
      title: "Privacy Policy",
      description: "Privacy Policy for DollarDeploy",
      image: "/api/og/privacy"
    });
  }

  if (path === "tos") {
    Object.assign(content, {
      title: "Terms of Service",
      description: "Terms of Service for DollarDeploy",
      image: "/api/og/tos"
    });
  }

  if (path === "team") {
    Object.assign(content, {
      title: "About us",
      description: "About us - DollarDeploy",
      image: "/api/og/team"
    });
  }

  if (title) {
    content.title = title;
  }

  if (description) {
    content.description = description;
  }

  if (image) {
    content.image = image;
  }

  return content;
};

export const SeoHead = ({
  path,
  title,
  description,
  image,
  children
}: {
  path: string;
  title?: string;
  description?: string;
  image?: string;
  children?: React.ReactNode;
}) => {
  const seo = getSeo(path, title, description, image);
  return (
    <Head>
      <title>
        {seo.title} - {seo.name}
      </title>
      <meta key="description" name="description" content={seo.description} />
      <meta key="og:description" property="og:description" content={seo.description} />
      <meta property="og:title" content={`${seo.title} | DollarDeploy`} key="title" />
      {seo.image && <meta name="og:image" content={seo.image} key="image" />}
      {seo.image && <meta name="twitter:image" content={seo.image} key="twitter:image" />}
      {children}
    </Head>
  );
};
